/**
 * LanguageSuggestionBanner — floating toast shown when the visitor's
 * country (via timezone) suggests a different language than the one on
 * screen. Message is written IN the suggested language. Dismissible,
 * remembered via localStorage.
 */
import React, { useState } from 'react';
import { Globe, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const SUGGEST = {
  en: { q: 'Prefer English?', cta: 'Switch to English' },
  es: { q: '¿Prefieres verlo en español?', cta: 'Cambiar a español' },
  fr: { q: 'Vous préférez le français ?', cta: 'Passer au français' },
  de: { q: 'Lieber auf Deutsch?', cta: 'Zu Deutsch wechseln' },
  tl: { q: 'Mas gusto mo ba sa Filipino?', cta: 'Lumipat sa Filipino' },
  pt: { q: 'Prefere em português?', cta: 'Mudar para português' },
  ar: { q: 'تفضّل العربية؟', cta: 'التبديل إلى العربية' },
};

export const LanguageSuggestionBanner = () => {
  const { lang, setLang, suggestedLang } = useLanguage();
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem('hibff_lang_hint_dismissed') === '1'
  );
  if (dismissed || !suggestedLang || suggestedLang === lang) return null;
  const s = SUGGEST[suggestedLang];
  if (!s) return null;
  const dismiss = () => {
    localStorage.setItem('hibff_lang_hint_dismissed', '1');
    setDismissed(true);
  };
  return (
    <div
      className="fixed bottom-4 right-4 z-50 flex items-center gap-2.5 pl-4 pr-2 py-2.5 rounded-full shadow-lg border bg-white"
      style={{ borderColor: '#E5D9FF' }}
      dir={suggestedLang === 'ar' ? 'rtl' : 'ltr'}
      data-testid="language-suggestion-banner"
    >
      <Globe size={15} style={{ color: '#7B4FD8' }} />
      <span className="text-sm" style={{ color: '#4A3580' }}>{s.q}</span>
      <button
        onClick={() => { setLang(suggestedLang); dismiss(); }}
        className="text-sm font-semibold underline hover:no-underline"
        style={{ color: '#7B4FD8' }}
        data-testid="language-suggestion-switch"
      >
        {s.cta}
      </button>
      <button
        onClick={dismiss}
        aria-label="Dismiss language suggestion"
        className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
        style={{ color: '#999' }}
        data-testid="language-suggestion-dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
};

export default LanguageSuggestionBanner;
